import { useMutation, useQuery } from "@apollo/client";
import { Checkbox, Col, Popover, Row, Spin } from "antd";
import CiqAgSearch from "components/ciq-ag-search";
import { ESubmittalStatus } from "constants/index";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import { ReactNode, useContext, useEffect, useState } from "react";
import { useParams } from "react-router";
import { MUTATION_SUBMITTAL_SUMMARY } from "services/graphQL/mutations";
import { QUERY_ACTIVE_PROJECT_USERS } from "services/graphQL/queries";

function formatSummaryList(value: unknown): string {
  if (!value) return "";
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object") {
          const record = item as Record<string, unknown>;
          if (typeof record.name === "string" && record.name) {
            return record.name;
          }
          const firstName =
            typeof record.first_name === "string" ? record.first_name : "";
          const lastName =
            typeof record.last_name === "string" ? record.last_name : "";
          return [firstName, lastName].filter(Boolean).join(" ");
        }
        return "";
      })
      .filter(Boolean)
      .join(", ");
  }
  return String(value);
}

function SummaryField({
  label,
  value,
  title,
  truncate = true
}: {
  label: string;
  value: ReactNode;
  title?: string;
  truncate?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-x-2 py-0.5 min-w-0">
      <span className="font-semibold shrink-0">{label}</span>
      <span
        className={`min-w-0 ${truncate ? "truncate" : "break-words"}`}
        title={title}
      >
        {value}
      </span>
    </div>
  );
}

export function CloseAndDistributeSubmittal(props: {
  submittalData: any;
  submittalWorkflowResponses?: any;
  reviewVal?: string;
  submittalViewState: {
    status: number;
    userType: string;
  };
  commentVal?: string;
  redistributeSubmittalToUsers?: any;
}) {
  const {
    submittalData,
    submittalWorkflowResponses,
    reviewVal,
    submittalViewState,
    commentVal,
    redistributeSubmittalToUsers
  } = props;
  const { gqlClientForProject }: TProjectContext = useContext(ProjectContext);
  const { projectId } = useParams() as any;
  const [submittalSummary, setSubmittalSummary] = useState<any>();
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [foundUsers, setFoundUsers] = useState([]);
  const [checkedUsers, setCheckedUsers] = useState<any>([]);

  const { data: usersData, loading } = useQuery(QUERY_ACTIVE_PROJECT_USERS, {
    fetchPolicy: "no-cache",
    client: gqlClientForProject,
    variables: {
      where: {
        project_id: { _eq: projectId },
        status_id: { _eq: 2 }
      }
    }
  });

  const [getSubmittalDistributionSummary] = useMutation<any>(
    MUTATION_SUBMITTAL_SUMMARY,
    {
      client: gqlClientForProject
    }
  );

  const retriveSubmittalSummary = async () => {
    setSummaryLoading(true);
    const submittalSummaryData: any = await getSubmittalDistributionSummary({
      variables: { submittalId: submittalData.id }
    });
    setSummaryLoading(false);
    if (submittalSummaryData?.data?.prepare_submittal_distribution_summary) {
      setSubmittalSummary(
        submittalSummaryData?.data?.prepare_submittal_distribution_summary
      );
    }
  };

  useEffect(() => {
    if (usersData?.project_users) {
      setFoundUsers(usersData?.project_users);
    }
  }, [usersData]);

  useEffect(() => {
    if (submittalViewState.status !== (ESubmittalStatus.DONE as number)) {
      retriveSubmittalSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittalData.id]);

  const reviewStatusObj = submittalWorkflowResponses?.filter(
    (item: any) => item.id === reviewVal
  );

  const onSearch = (searchTxt: string) => {
    if (searchTxt !== "") {
      const results = usersData?.project_users?.filter((user: any) => {
        return `${user?.user?.first_name} ${user?.user?.last_name}`
          .toLowerCase()
          .includes(searchTxt.toLowerCase());
        // Use the toLowerCase() method to make it case-insensitive
      });
      setFoundUsers(results);
    } else {
      setFoundUsers(usersData?.project_users);
    }

    setSearchText(searchTxt);
  };

  // const alternateFromApi = formatSummaryList(
  //   submittalSummary?.alternateGcReviewers
  // );
  // const gcReviewersFromApi = formatSummaryList(submittalSummary?.gcReviewers);
  // const mainGcReviewer = submittalSummary?.mainGcReviewer || "";
  // const alternateGcReviewersDisplay =
  //   alternateFromApi ||
  //   gcReviewersFromApi
  //     .split(",")
  //     .map((name) => name.trim())
  //     .filter((name) => name && name !== mainGcReviewer)
  //     .join(", ");
  const submittersDisplay = formatSummaryList(submittalSummary?.submitters);
  const designReviewersDisplay = formatSummaryList(
    submittalSummary?.designReviewers
  );

  const getDistributionUsers = () => {
    const distributiongroupsandusers =
      submittalSummary?.submittalDLDisplayList || [];
    const dlGroups: any[] = submittalSummary?.submittalDGDisplayList || [];
    const popupContent = (users: []) => (
      <div className="flex-col border-solid border-[1px] p-1 space-y-1 border-gray-300 overflow-y-auto max-h-64">
        {users.map((item: any) => {
          return (
            <div
              key={item?.name}
              className="flex-col px-2 border-solid  border-[1px] border-gray-300 min-w-[100px]  max-w-[150px] truncate"
            >
              {item?.name}
            </div>
          );
        })}
      </div>
    );

    return (
      <div className="flex flex-wrap w-full gap-x-2">
        {distributiongroupsandusers.map((user: any, index: number) => {
          const dlUsers = dlGroups.find((usr: any) => usr.name === user.name);
          return user.type === "distribution_group" ? (
            <div className="flex min-w-fit cursor-pointer">
              <Popover content={popupContent(dlUsers?.users || [])}>
                {distributiongroupsandusers.length > 0 &&
                index <= distributiongroupsandusers.length - 2
                  ? `${user?.name},`
                  : user?.name}
              </Popover>
            </div>
          ) : (
            <div className="flex min-w-fit">
              {distributiongroupsandusers.length > 0 &&
              index <= distributiongroupsandusers.length - 2
                ? `${user?.name},`
                : user?.name}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex justify-center items-center">
      {loading || summaryLoading ? (
        <Spin />
      ) : (
        <div className="flex w-full h-full">
          {submittalViewState.status !== (ESubmittalStatus.DONE as number) && (
            <Col className="flex-col w-full h-full border-solid border-[1px] border-gray-300 p-2 px-2 space-y-2">
              <Row className="flex-col border-solid border-[1px] border-gray-300 px-2">
                <SummaryField
                  label="GC Reviewer:"
                  value={submittalSummary?.mainGcReviewer || ""}
                />
                <SummaryField
                  label="Final Response:"
                  value={
                    (reviewStatusObj?.length > 0 &&
                      reviewStatusObj[0].response) ||
                    submittalSummary?.latestResponse ||
                    ""
                  }
                />
                <div className="flex gap-x-2 py-0.5 min-w-0">
                  <span className="font-semibold shrink-0">Comment:</span>
                  <textarea
                    readOnly
                    className="w-full border-gray-300 mb-2 pl-1 outline-none"
                    value={commentVal || submittalSummary?.latestComment || ""}
                  />
                </div>
              </Row>
              <Row className="flex-col border-solid border-[1px] border-gray-300 px-2">
                <SummaryField
                  label="Responsible Contractor Company:"
                  value={
                    submittalSummary?.responsibleContractorCompanyName || ""
                  }
                  title={
                    submittalSummary?.responsibleContractorCompanyName || ""
                  }
                  truncate={false}
                />
                <SummaryField
                  label="Submitter(s):"
                  value={submittersDisplay}
                  title={submittersDisplay}
                />
              </Row>
              <Row className="flex-col border-solid border-[1px] border-gray-300 px-2">
                <SummaryField
                  label="Design Reviewer Company:"
                  value={submittalSummary?.designReviewerCompanyName || ""}
                  title={submittalSummary?.designReviewerCompanyName || ""}
                  truncate={false}
                />
                <SummaryField
                  label="Design Reviewer(s):"
                  value={designReviewersDisplay}
                  title={designReviewersDisplay}
                />
              </Row>
              {/* <Row className="flex-col border-solid border-[1px] border-gray-300 px-2">
                <SummaryField
                  label="Alternate GC Reviewer(s):"
                  value={alternateGcReviewersDisplay}
                  title={alternateGcReviewersDisplay}
                />
              </Row> */}
              <Row className="flex-col border-solid border-[1px] border-gray-300 px-2">
                <div className="flex gap-x-2 py-0.5 min-w-0">
                  <span className="font-semibold shrink-0">
                    Distribution List:
                  </span>
                  <div className="min-w-0">{getDistributionUsers()}</div>
                </div>
              </Row>
            </Col>
          )}
          {submittalViewState.status === (ESubmittalStatus.DONE as number) && (
            <div className="flex-col w-full space-y-2">
              <CiqAgSearch
                value={searchText}
                onSearch={onSearch}
                className="w-full"
                placeholder="Search here"
                data-testid="index-input"
              />
              <Checkbox.Group className="flex w-full">
                <Col
                  span={50}
                  className="flex-col w-full overflow-y-auto max-h-80 border-solid border-[1px] border-gray-300 px-2 py-2 space-y-2"
                >
                  {foundUsers && foundUsers?.length === 0 && (
                    <p className="mt-2 w-full text-center">No user found</p>
                  )}
                  {foundUsers?.length > 0 &&
                    foundUsers.map((item: any) => {
                      return (
                        <Row key={item?.user?.email} className="flex w-full">
                          <div className="flex w-full p-2 border-solid border-[1px] border-gray-300">
                            <Checkbox
                              value={item?.user?.email}
                              data-testid="index-checkbox"
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setCheckedUsers([
                                    ...checkedUsers,
                                    e.target.value
                                  ]);

                                  redistributeSubmittalToUsers([
                                    ...checkedUsers,
                                    e.target.value
                                  ]);
                                } else {
                                  const checkedUserArr = checkedUsers.filter(
                                    (email: string) => email !== e.target.value
                                  );
                                  redistributeSubmittalToUsers(checkedUserArr);
                                  setCheckedUsers(checkedUserArr);
                                }
                              }}
                            >
                              {`${item?.user?.first_name} ${item?.user?.last_name}`}
                            </Checkbox>
                          </div>
                        </Row>
                      );
                    })}
                </Col>
              </Checkbox.Group>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
